import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { MdTune } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Empty, Input, Switch } from "antd";
import { toast } from "react-toastify";
import {
  propertyDetailsApi,
  savePropertyTypesApi,
} from "../../../services/propertyType";
import LoaderMu from "../../../components/common/LoaderMu";
import { useSelector } from "react-redux";
import NotAuthorized from "../../../components/common/NotAuthorized";
import "./DetailsConfig.scss";

const getFieldStatus = (config) => {
  if (!config.enabled) {
    return { label: "Disabled", className: "details-config__status--disabled" };
  }
  if (config.mandatory) {
    return { label: "Required", className: "details-config__status--required" };
  }
  return { label: "Optional", className: "details-config__status--optional" };
};

export const DetailsConfig = () => {
  const roleDetails = useSelector(
    (state) => state?.rolesData?.masterPermissionResponseDTOList
  );

  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    if (roleDetails) {
      const permissions = roleDetails.find((detail) => {
        return detail.childPermissions.find(
          (childPerm) =>
            childPerm?.key === "property_type_property_details" &&
            childPerm?.status === true
        );
      });
      setIsAuthorized(!!permissions);
    } else {
      setIsAuthorized(true);
    }
  }, [roleDetails]);

  const navigate = useNavigate();
  const [propertyTypeList, setPropertyTypeList] = useState([]);
  const [propertyTypeTitle, setPropertyTypeTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fieldConfig, setFieldConfig] = useState({});

  const { id } = useParams();

  const fetchDetailsList = () => {
    setLoading(true);
    propertyDetailsApi(id)
      .then(async (response) => {
        setPropertyTypeList(response?.propertyDetail ?? []);
        setPropertyTypeTitle(
          response?.title?.en || response?.title?.ar || ""
        );

        const config = {};
        response?.propertyDetail?.forEach((item) => {
          config[item.field_name] = {
            enabled: item.visible !== false,
            mandatory: item.mandatory === "Yes",
          };
        });
        setFieldConfig(config);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error?.response?.data?.message);
      });
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchDetailsList();
    } else {
      setLoading(false);
    }
  }, [isAuthorized]);

  const filteredFields = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return propertyTypeList;

    return propertyTypeList.filter((item) => {
      const label = item?.field_level?.en?.toLowerCase() ?? "";
      const key = item?.field_name?.toLowerCase() ?? "";
      return label.includes(query) || key.includes(query);
    });
  }, [propertyTypeList, searchQuery]);

  const stats = useMemo(() => {
    const values = Object.values(fieldConfig);
    return {
      total: propertyTypeList.length,
      enabled: values.filter((cfg) => cfg.enabled).length,
      mandatory: values.filter((cfg) => cfg.enabled && cfg.mandatory).length,
    };
  }, [fieldConfig, propertyTypeList.length]);

  const handleEnableToggle = (fieldName, checked) => {
    setFieldConfig((prev) => ({
      ...prev,
      [fieldName]: {
        enabled: checked,
        mandatory: checked ? (prev[fieldName]?.mandatory ?? false) : false,
      },
    }));
  };

  const handleMandatoryToggle = (fieldName, checked) => {
    setFieldConfig((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        mandatory: checked,
      },
    }));
  };

  const handleUpdate = () => {
    setSaving(true);
    const payload = propertyTypeList.map((item) => {
      const cfg = fieldConfig[item.field_name] ?? {
        enabled: false,
        mandatory: false,
      };
      return {
        fieldName: item.field_name,
        visible: cfg.enabled,
        mandatory: cfg.mandatory,
      };
    });

    savePropertyTypesApi(id, payload)
      .then((response) => {
        setSaving(false);
        navigate(-1);
        toast.success(response?.message);
      })
      .catch((error) => {
        setSaving(false);
        toast.error(error?.response?.data?.message);
      });
  };

  return (
    <div className="d-flex flex-column pageContainer p-3 h-100 details-config">
      <LoaderMu loading={loading} />
      <div className="flex-grow-1 h-100 pageContent position-relative pt-4 overflow-auto">
        {!loading && !isAuthorized ? (
          <NotAuthorized />
        ) : (
          <Card className="h-100 bg-white shadow border-0 theme-card-cover">
            <div className="theme-card-header px-1 border-bottom border-light">
              <Stack
                direction="horizontal"
                gap={2}
                className="flex-wrap justify-content-between px-3 pt-3 pb-2 align-items-start"
              >
                <div>
                  <h5 className="mb-0 position-relative fs-18 text-black fw-medium">
                    <div className="align-items-center bg-primary d-inline-flex h-60 justify-content-center position-absolute rounded start-0 text-white theme-icon-box w-60 z-1">
                      <MdTune size={36} />
                    </div>
                    Details Config
                  </h5>
                  {propertyTypeTitle && (
                    <p className="details-config__subtitle mb-0">
                      {propertyTypeTitle}
                    </p>
                  )}
                </div>
              </Stack>
            </div>

            <div className="flex-grow-1 d-flex flex-column px-3 pb-3 h-100 overflow-auto mt-4">
              <Alert
                type="info"
                showIcon
                className="mb-4"
                message="Configure which fields agencies see when listing this property type."
                description="Enable a field to show it in the listing form. Mark it as mandatory to require agencies to fill it in. Disabled fields are hidden from agencies."
              />

              {propertyTypeList.length > 0 && (
                <div className="details-config__toolbar mb-4">
                  <Row className="g-3 align-items-center">
                    <Col xs={12} lg={5}>
                      <Input.Search
                        allowClear
                        placeholder="Search fields by name..."
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        size="large"
                      />
                    </Col>
                    <Col xs={12} lg={7}>
                      <Stack
                        direction="horizontal"
                        gap={3}
                        className="flex-wrap justify-content-lg-end"
                      >
                        <div className="details-config__stat text-center">
                          <div className="details-config__stat-value text-dark">
                            {stats.total}
                          </div>
                          <div className="details-config__stat-label">Total</div>
                        </div>
                        <div className="details-config__stat text-center">
                          <div className="details-config__stat-value text-success">
                            {stats.enabled}
                          </div>
                          <div className="details-config__stat-label">
                            Enabled
                          </div>
                        </div>
                        <div className="details-config__stat text-center">
                          <div className="details-config__stat-value text-warning">
                            {stats.mandatory}
                          </div>
                          <div className="details-config__stat-label">
                            Mandatory
                          </div>
                        </div>
                      </Stack>
                    </Col>
                  </Row>
                </div>
              )}

              {propertyTypeList.length > 0 ? (
                <>
                  <div className="details-config__table-wrap flex-grow-1">
                    <div className="table-responsive">
                      <table className="table details-config__table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th className="text-center">Status</th>
                            <th className="details-config__toggle-col">
                              Enabled
                            </th>
                            <th className="details-config__toggle-col">
                              Mandatory
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredFields.length > 0 ? (
                            filteredFields.map((element, index) => {
                              const fieldName = element?.field_name;
                              const config = fieldConfig[fieldName] ?? {
                                enabled: false,
                                mandatory: false,
                              };
                              const status = getFieldStatus(config);

                              return (
                                <tr
                                  key={fieldName ?? index}
                                  className={
                                    !config.enabled
                                      ? "details-config__row--disabled"
                                      : ""
                                  }
                                >
                                  <td>
                                    <div className="details-config__field-name">
                                      {element?.field_level?.en}
                                    </div>
                                    <div className="details-config__field-key">
                                      {fieldName}
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <span
                                      className={`details-config__status ${status.className}`}
                                    >
                                      {status.label}
                                    </span>
                                  </td>
                                  <td className="details-config__toggle-col">
                                    <Switch
                                      checked={config.enabled}
                                      checkedChildren="On"
                                      unCheckedChildren="Off"
                                      onChange={(checked) =>
                                        handleEnableToggle(fieldName, checked)
                                      }
                                    />
                                  </td>
                                  <td className="details-config__toggle-col">
                                    <Switch
                                      checked={config.mandatory}
                                      disabled={!config.enabled}
                                      checkedChildren="Yes"
                                      unCheckedChildren="No"
                                      onChange={(checked) =>
                                        handleMandatoryToggle(
                                          fieldName,
                                          checked
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center py-5">
                                <Empty
                                  description={`No fields match "${searchQuery}"`}
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="details-config__actions">
                    <Stack
                      direction="horizontal"
                      gap={2}
                      className="justify-content-between flex-wrap"
                    >
                      <div className="text-muted fs-14 align-self-center">
                        {stats.enabled} of {stats.total} fields enabled
                        {stats.mandatory > 0 &&
                          ` · ${stats.mandatory} required`}
                      </div>
                      <Stack direction="horizontal" gap={2}>
                        <Button
                          variant="outline-secondary"
                          onClick={() => navigate(-1)}
                          className="rounded-xs fs-15"
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleUpdate}
                          className="rounded-xs fs-15"
                          type="submit"
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                      </Stack>
                    </Stack>
                  </div>
                </>
              ) : (
                !loading && (
                  <Empty description="No property detail fields available" />
                )
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
